import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2, UserCog } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RoleGate } from "@/components/auth/RoleGate";
import { usersService } from "@/services/users.service";
import { getErrorMessage } from "@/services/api";
import { ROLE_LABELS } from "@/config/constants";
import { formatDate, initialsOf } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/contexts/AuthContext";

export default function UsersListPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();
    const { user: me } = useAuth();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ sortBy: "created_at", sortOrder: "desc" });
    const [confirm, setConfirm] = useState(null);

    const query = useQuery({
        queryKey: ["users", { page, search, ...sort }],
        queryFn: () =>
            usersService.list({ page, pageSize: 20, search, sortBy: sort.sortBy, sortOrder: sort.sortOrder }),
    });

    const del = useMutation({
        mutationFn: (id) => usersService.remove(id),
        onSuccess: () => {
            toast.success("Usuário removido.");
            qc.invalidateQueries({ queryKey: ["users"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Não foi possível remover usuário.")),
    });

    const columns = [
        {
            key: "name",
            header: "Usuário",
            sortable: true,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                            {initialsOf(r.name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{r.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{r.email}</div>
                    </div>
                </div>
            ),
        },
        {
            key: "role",
            header: "Perfil",
            cell: (r) => (
                <Badge variant="secondary" className="text-[10px]" data-testid={`users-role-${r.id}`}>
                    {ROLE_LABELS[r.role] || r.role}
                </Badge>
            ),
        },
        {
            key: "status",
            header: "Status",
            cell: (r) =>
                r.status === "active" ? (
                    <Badge variant="outline" className="border-success/40 text-success">Ativo</Badge>
                ) : (
                    <Badge variant="outline" className="text-muted-foreground">Inativo</Badge>
                ),
        },
        {
            key: "created_at",
            header: "Criado em",
            sortable: true,
            cell: (r) => <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>,
        },
        {
            key: "actions",
            header: "",
            className: "w-24",
            cell: (r) => (
                <div className="flex justify-end gap-1">
                    <RoleGate resource="users" action="update">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/users/${r.id}`);
                            }}
                            aria-label="Editar"
                            data-testid={`users-edit-${r.id}`}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                    <RoleGate resource="users" action="delete">
                        {r.id !== me?.id && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-danger hover:text-danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirm(r);
                                }}
                                aria-label="Excluir"
                                data-testid={`users-delete-${r.id}`}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </RoleGate>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Usuários"
                description="Gerencie usuários e suas permissões de acesso."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "Usuários" }]}
            />
            <DataTable
                dataTestId="users"
                columns={columns}
                query={query.data}
                loading={query.isLoading}
                onSearch={(t) => {
                    setPage(1);
                    setSearch(t);
                }}
                onPageChange={setPage}
                onSortChange={setSort}
                onCreate={can("users", "create") ? () => navigate("/users/new") : null}
                createLabel="Novo usuário"
                onRowClick={(r) => can("users", "update") && navigate(`/users/${r.id}`)}
                emptyState={{ title: "Sem usuários", description: "Cadastre o primeiro usuário.", cta: "Novo usuário" }}
                initialSort={sort}
            />
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Excluir usuário?"
                description={`Esta ação é permanente. \"${confirm?.name || ""}\" será removido.`}
                confirmLabel="Excluir"
                variant="destructive"
                onConfirm={() => {
                    if (confirm) del.mutate(confirm.id);
                    setConfirm(null);
                }}
            />
        </div>
    );
}
